<template>
    <div class="error-msg-wrap">
        <div class="form-wrapper slide-up" style="width:560px">
            <div class="form-close">
                <button type="button" class="form-close-x" title="Close" @click="$emit('close')">&times;</button>
            </div>
            <h2 class="sb-modal-title">{{ metric ? metric.Name : 'Numbers for ' + weekRangeNumbered(week) }}</h2>
            <p class="sb-intro">{{ metric ? ownerName(metric) + ' · goal ' + goalText(metric, week) + '. Fix a past week here if a number was wrong.' : 'Green means on goal.' }}</p>

            <div v-if="!metric && myMetrics.length && myMetrics.length < metrics.length" class="sb-seg">
                <button type="button" :class="onlyMine ? 'on' : ''" @click="onlyMine = true">Mine ({{ myMetrics.length }})</button>
                <button type="button" :class="!onlyMine ? 'on' : ''" @click="onlyMine = false">{{ team ? 'Whole ' + teamLabel(team) + ' Scoreboard' : 'All Found' }}</button>
            </div>

            <div class="sb-entry">
                <div v-for="row in rows" :key="row.key" class="sb-erow">
                    <div class="sb-e-l">
                        <b>{{ row.title }}</b>
                        <span>{{ row.sub }}</span>
                    </div>
                    <div class="sb-e-in" :class="stateClass(row)">
                        <input type="number" step="any" v-model="values[row.key]" placeholder="—" :aria-label="row.title" />
                        <em v-if="row.metric.Unit">{{ row.metric.Unit }}</em>
                    </div>
                </div>
                <p v-if="!rows.length" class="sb-intro">No metrics here yet.</p>
            </div>

            <div class="sb-actions">
                <span style="flex:1"></span>
                <div class="btn-light" @click="$emit('close')">Cancel</div>
                <div class="btn" :class="saving ? 'disable-button' : ''" @click="save()">Save Numbers</div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import axios from 'axios';
import { passes, goalText, formatValue, weekRangeNumbered, relativeWeekLabel, addDays } from '@/scoreboard';

export default defineComponent({
    name: 'ScoreboardEntryModal',
    // Either a team's numbers for this week (metrics + team), or one metric's last 4 weeks (metric).
    // week: this week's Monday.
    props: [ 'metrics', 'team', 'metric', 'week', 'employees', 'myEmployeeID' ],
    emits: [ 'close', 'saved' ],
    data() {
        return {
            values: {} as any,
            onlyMine: false,
            saving: false
        }
    },
    created() {
        // Starts on your own numbers when you own some of the team's
        this.onlyMine = !this.metric && this.myMetrics.length > 0 && this.myMetrics.length < (this.metrics || []).length;
        for(const row of this.allRows) {
            const v = row.metric.Values[row.week];
            this.values[row.key] = v === undefined || v === null ? '' : v;
        }
    },
    computed: {
        myMetrics(): any[] {
            return (this.metrics || []).filter((m: any) => this.myEmployeeID && m.OwnerEmployeeID == this.myEmployeeID);
        },
        allRows(): any[] {
            if(this.metric) {
                return [ 0, 1, 2, 3 ].map(i => {
                    const week = addDays(this.week, -7 * i);
                    return {
                        key: `${this.metric.ID}|${week}`,
                        metric: this.metric,
                        week,
                        title: relativeWeekLabel(week),
                        sub: weekRangeNumbered(week)
                    };
                });
            }
            const lastWeek = addDays(this.week, -7);
            return (this.metrics || []).map((m: any) => ({
                key: `${m.ID}|${this.week}`,
                metric: m,
                week: this.week,
                title: m.Name,
                sub: `${this.ownerName(m)} · goal ${goalText(m, this.week)} · last week ${m.Values[lastWeek] !== undefined ? formatValue(m, m.Values[lastWeek]) : '—'}`
            }));
        },
        rows(): any[] {
            if(this.onlyMine) {
                return this.allRows.filter((r: any) => r.metric.OwnerEmployeeID == this.myEmployeeID);
            }
            return this.allRows;
        }
    },
    methods: {
        weekRangeNumbered,
        goalText,
        ownerName(m: any): string {
            const e = (this.employees || []).find((emp: any) => emp.ID == m.OwnerEmployeeID);
            return e ? e.Name : 'No owner';
        },
        stateClass(row: any): string {
            const p = passes(row.metric, this.values[row.key], row.week);
            return p === null ? '' : p ? 'pass' : 'fail';
        },
        async save() {
            if(this.saving) {
                return;
            }
            // Only what changed, including cleared numbers
            const entries = this.allRows.filter((row: any) => {
                const before = row.metric.Values[row.week];
                const now = this.values[row.key];
                return String(before === undefined || before === null ? '' : before) !== String(now);
            }).map((row: any) => ({ MetricID: row.metric.ID, Week: row.week, Value: this.values[row.key] }));
            if(!entries.length) {
                this.$emit('close');
                return;
            }
            this.saving = true;
            await axios.post(`${this.$store.state.apiUrl}/api/scoreboard/values`, { Values: entries })
                .then(() => {
                    this.$emit('saved');
                })
                .catch(e => {
                    console.log(e);
                    alert('There was an error saving these numbers');
                })
            this.saving = false;
        }
    }
});
</script>

<style scoped>
.sb-modal-title { margin: 0 0 6px; font-size: 20px; }
.sb-intro { font-size: 13.5px; color: var(--font-dark-gray); margin: 0 0 14px; }
.sb-seg { display: inline-flex; background: #fff; border: 1px solid var(--mid-gray); border-radius: 8px; padding: 2px; margin-bottom: 12px; }
.sb-seg button { background: none; border: none; font-family: inherit; font-size: 12.5px; color: var(--font-dark-gray); padding: 6px 13px; border-radius: 6px; cursor: pointer; }
.sb-seg button.on { background: var(--primary-color); color: #fff; font-weight: 600; }
.sb-entry { max-height: 52vh; overflow: auto; }
.sb-erow { display: flex; align-items: center; gap: 12px; padding: 10px 0; border-top: 1px solid var(--mid-gray); }
.sb-erow:first-child { border-top: none; }
.sb-e-l { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.sb-e-l b { font-size: 14px; }
.sb-e-l span { font-size: 12px; color: var(--font-mid-gray); }
.sb-e-in { display: flex; align-items: center; gap: 6px; border: 1.5px solid var(--mid-gray); border-radius: 9px; padding: 0 10px; background: #fff; width: 140px; flex-shrink: 0; }
.sb-e-in input { width: 100%; border: none !important; outline: none; padding: 9px 0; margin: 0; font-size: 16px; font-weight: 700; text-align: right; background: none; }
.sb-e-in em { font-style: normal; font-size: 12px; color: var(--font-mid-gray); }
.sb-e-in.pass { border-color: #2E7D46; background: #E3F4E8; }
.sb-e-in.fail { border-color: var(--primary-color); background: #FCEBEC; }
.sb-actions { display: flex; align-items: center; gap: 10px; margin-top: 20px; }
.sb-actions .btn, .sb-actions .btn-light { margin: 0; padding: 8px 18px; }
</style>
